/**
 * The pay calendar — the one place per-paycheck money becomes monthly money.
 *
 * Three axes exist, and until this module every one of them was called
 * "monthly":
 *
 *   per check         annual / N              the funding instruction
 *   guaranteed month  perCheck × floor(N/12)  what you can fund every month
 *   accrual month     annual / 12             what the thing actually costs
 *
 * The bug this module exists to kill was a single `× 2`. `paychecksPerMonth()`
 * returned `floor(paycheckCount / 12)` — 2 for both a 24 and a 26 schedule —
 * and that scalar was then used as a *unit conversion*. For 24 it happens to
 * be right (24/12 = 2 exactly). For 26 it is 7.69% short, because 26/12 is
 * 2.1667, and the two missing paychecks a year are precisely the gap.
 *
 * The floor is not wrong; it was answering a different question. "How many
 * paychecks is every month guaranteed to contain?" is a feasibility question,
 * and its answer really is 2 — a YNAB target you can only hit in a 3-paycheck
 * month is a target you miss ten times a year. "What is this per-check figure
 * per month?" is a conversion question, and its answer is never 2 unless the
 * schedule pays 24. Two questions, two functions, both correct; the failure was
 * one function serving both.
 *
 * ── The invariant ──
 *
 * Annual is the only axis where all three agree, so annual is what closes:
 *
 *   perCheck(a, s) × s.paycheckCount === a        for every schedule
 *   accrualMonth(a) × 12             === a
 *   Σ checksInMonth(s, y, m) for m 0..11 === s.paycheckCount
 *
 * That last one is the test the old code could never have passed, and it is
 * why it is a property test rather than a comment.
 */

import type { FundingSchedule } from './types.js';

/** Which monthly figure a view is quoting. */
export type NetBasis = 'accrual' | 'guaranteed';

/**
 * Fallback cadence when nothing has synced yet.
 *
 * Semi-monthly rather than bi-weekly on purpose: 24 is the cadence where the
 * accrual and guaranteed axes coincide, so an un-synced app shows one
 * unambiguous number instead of silently picking a side.
 */
export const FALLBACK_PAYCHECK_COUNT = 24;

const countOf = (s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined): number => {
  const n = Number(s?.paycheckCount);
  return Number.isFinite(n) && n > 0 ? n : FALLBACK_PAYCHECK_COUNT;
};

// ───────────────────────── the two factors ─────────────────────────

/**
 * Paychecks every month is guaranteed to contain — a feasibility answer.
 *
 * Use for "can I fund this target in any month?", never to convert units.
 * A bi-weekly year pays 26 times, but ten of those months hold two paychecks
 * and two hold three; the floor is the number you can actually budget against
 * and the extras are surplus.
 */
export const guaranteedChecksPerMonth = (
  s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined,
): number => Math.floor(countOf(s) / 12);

/**
 * The per-check → monthly conversion factor. Never 2 unless the schedule is 24.
 *
 * This is the number the app was missing. 26/12 = 2.1667.
 */
export const monthlyFactor = (
  s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined,
): number => countOf(s) / 12;

// ───────────────────────── the three axes ─────────────────────────

/** What moves each payday. The funding instruction. */
export const perCheck = (
  annual: number,
  s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined,
): number => annual / countOf(s);

/** What the thing costs. The axis CSP, the Sankey and net worth all speak. */
export const accrualMonth = (annual: number): number => annual / 12;

/** What can be funded in any month, including the ten that hold two checks. */
export const guaranteedMonth = (
  annual: number,
  s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined,
): number => perCheck(annual, s) * guaranteedChecksPerMonth(s);

/** Turn a per-check figure into its true monthly equivalent. */
export const checkToMonth = (
  amountPerCheck: number,
  s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined,
): number => amountPerCheck * monthlyFactor(s);

/** Turn a per-check figure into the monthly figure every month can cover. */
export const checkToGuaranteedMonth = (
  amountPerCheck: number,
  s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined,
): number => amountPerCheck * guaranteedChecksPerMonth(s);

/** Annualise a per-check figure. The reconciler. */
export const checkToAnnual = (
  amountPerCheck: number,
  s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined,
): number => amountPerCheck * countOf(s);

/** Pick the monthly figure a basis is asking for. */
export const monthOnBasis = (
  annual: number,
  s: Pick<FundingSchedule, 'paycheckCount'> | null | undefined,
  basis: NetBasis,
): number => (basis === 'accrual' ? accrualMonth(annual) : guaranteedMonth(annual, s));

// ───────────────────────── the real calendar ─────────────────────────

/** UTC midnight for a Y-M-D, so a timezone can't shift a payday across a month. */
const utc = (y: number, m: number, d: number): number => Date.UTC(y, m, d);

/** Normalise a possibly-overflowing month index to a concrete year/month. */
export function normaliseMonth(year: number, month: number): { year: number; month: number } {
  const t = year * 12 + month;
  return { year: Math.floor(t / 12), month: ((t % 12) + 12) % 12 };
}

/**
 * How many paychecks actually land in one calendar month.
 *
 * Positional cadences (monthly, semi-monthly) are the same every month by
 * construction, so they answer from `paycheckCount / 12` and ignore the
 * anchor. Interval cadences (weekly, bi-weekly) genuinely vary — a bi-weekly
 * year has ten 2-check months and two 3-check months, and *which* months those
 * are depends entirely on where the first payday fell. Without an anchor there
 * is no honest answer, so the guaranteed floor is returned rather than a guess
 * that would put the extra checks in the wrong month.
 *
 * The two extra paychecks are not a bonus. They are what makes the set-aside
 * arithmetic close: funding `annual/26` on all 26 checks yields exactly
 * `annual`. Smearing them across twelve equal months is what made ten months a
 * year quietly look more affordable than they are.
 *
 * ── The 27-paycheck year ──
 *
 * `paycheckCount` is a *nominal* rate, not a promise about any given calendar
 * year. A 14-day cadence fires 365.25/14 = 26.089 times a year on average, so
 * roughly one year in eleven contains 27 paydays rather than 26. This is the
 * real thing payroll departments plan around, and it is deliberately not
 * normalised away here: the money genuinely arrives.
 *
 * It also cannot hurt the set-aside. Notion divides by the nominal 26, so a
 * 27-payday year funds 103.8% of the annual cost — over-funding, never under.
 * The extra check is surplus, which is exactly what the guaranteed axis has
 * been saying all along.
 */
export function checksInMonth(
  s: Pick<FundingSchedule, 'paycheckCount' | 'anchorDate'> | null | undefined,
  year: number,
  month: number,
): number {
  const count = countOf(s);
  const perMonth = count / 12;

  // Positional cadence: an integer number of paydays every month, always.
  if (Number.isInteger(perMonth)) return perMonth;

  const anchor = s?.anchorDate ? Date.parse(`${String(s.anchorDate).slice(0, 10)}T00:00:00Z`) : NaN;
  if (!Number.isFinite(anchor)) return Math.floor(perMonth);

  // Interval cadence — step the true period across the month and count.
  // 365.25/count days rounds to 14 for 26/yr and 7 for 52/yr, which is what
  // these cadences actually are; deriving it keeps a 13-per-year schedule
  // (four-weekly) working without a special case.
  const intervalDays = Math.round(365.25 / count);
  const dayMs = 86_400_000;
  const period = intervalDays * dayMs;

  const { year: y, month: m } = normaliseMonth(year, month);
  const start = utc(y, m, 1);
  const end = utc(y, m + 1, 1); // exclusive; Date.UTC rolls month 12 into next year

  // First payday on or after `start`, walking the fixed period from the anchor.
  const stepsToStart = Math.ceil((start - anchor) / period);
  let t = anchor + stepsToStart * period;

  let n = 0;
  while (t < end) { n++; t += period; }
  return n;
}

/**
 * A schedule's paychecks across `months` starting at a given month.
 *
 * Returned as an array rather than summed so the projection can pay each month
 * what it is actually owed, and so a chart can mark the 3-check months.
 */
export function checkCalendar(
  s: Pick<FundingSchedule, 'paycheckCount' | 'anchorDate'> | null | undefined,
  startYear: number,
  startMonth: number,
  months: number,
): number[] {
  const out: number[] = [];
  for (let i = 0; i < months; i++) out.push(checksInMonth(s, startYear, startMonth + i));
  return out;
}

// ───────────────────────── lookup ─────────────────────────

export type ScheduleIndex = Map<string, FundingSchedule>;

export const indexSchedules = (schedules: FundingSchedule[] = []): ScheduleIndex =>
  new Map((schedules ?? []).map(s => [s.id, s]));

/**
 * The schedule a line is funded on.
 *
 * Falls back to the sole active schedule when a line has no relation — with
 * one cadence that is unambiguous, and it keeps a half-configured Notion
 * usable. With two live cadences there is no safe guess, so `undefined` is
 * returned and `countOf` applies the 24 fallback, where both axes agree and
 * the figure is at least self-consistent.
 */
export function scheduleFor(
  index: ScheduleIndex,
  fundingScheduleId: string | null | undefined,
  schedules: FundingSchedule[] = [],
): FundingSchedule | undefined {
  const direct = fundingScheduleId ? index.get(fundingScheduleId) : undefined;
  if (direct) return direct;
  const active = (schedules ?? []).filter(s => s.isActive && s.paycheckCount > 0);
  return active.length === 1 ? active[0] : undefined;
}
